import {
  BackupPolicyType,
  ScheduleMode,
  type BackupPolicyConfig,
} from '@repo/api-gw-sdk';

import { getResourcesScopeType } from '@/components/resourcesSelection/resourceSelectionDynamicViewer';
import { Wizard } from '@/components/wizard/Wizard';
import {
  createAllIncludedCondition,
  createAllExcludedCondition,
} from '@/types/advanceFilter';

import {
  EditBackupPolicyFlow,
  type BackupPolicyEditState,
} from './BackupPolicyCreationFlow';

interface BackupPolicyPanelProps {
  entity: BackupPolicyConfig;
  onSave: (policy: BackupPolicyConfig) => Promise<void>;
  onAbort: () => void;
}

export default function BackupPolicyPanel(props: BackupPolicyPanelProps) {
  const save = async (state: BackupPolicyEditState) => {
    const condition =
      state.resourcesScopeType === 'allIncluded'
        ? createAllIncludedCondition()
        : state.resourcesScopeType === 'allExcluded'
          ? createAllExcludedCondition()
          : state.condition;

    if (!condition) {
      return false;
    }

    await props.onSave({
      ...props.entity,
      name: state.name,
      condition: condition,
      schedules: state.schedules ?? [],
      include: state.include ?? [],
      exclude: state.exclude ?? [],
      scheduleMode: state.mode,
      policyType: state.policyType,
      resourceTypes: state.resourceTypes,
    });

    return true;
  };

  return (
    <Wizard<BackupPolicyEditState>
      onAbort={props.onAbort}
      hideBackArrow={true}
      onFinish={save}
      flow={
        props.entity.id ? EditBackupPolicyFlow.next! : [EditBackupPolicyFlow]
      }
      initialState={{
        id: props.entity.id,
        name: props.entity.name,
        condition: props.entity.condition,
        schedules: props.entity.schedules,
        include: props.entity.include || [],
        exclude: props.entity.exclude || [],
        resourcesScopeType: props.entity.condition
          ? getResourcesScopeType(props.entity.condition)
          : 'useConditions',
        mode:
          !props.entity.scheduleMode ||
          props.entity.scheduleMode === ScheduleMode.SCHEDULE_MODE_UNSPECIFIED
            ? ScheduleMode.SCHEDULE_MODE_BASIC
            : props.entity.scheduleMode,
        policyType:
          props.entity.policyType ||
          BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD,
        resourceTypes: props.entity.resourceTypes,
      }}
      title={props.entity.id ? 'Edit backup policy' : 'Create backup policy'}
      stepperLabels={[
        'Resource selection',
        'Resource list',
        'Backup schedules',
      ]}
    />
  );
}
